import { Component, inject, input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, AbstractControl, NgControl } from '@angular/forms';
import { CurrencyService } from '../../services/currency.service';
import { InputNumber } from '@openng/optimus-ui/inputnumber';
import { InputGroup } from '@openng/optimus-ui/inputgroup';
import { InputGroupAddon } from '@openng/optimus-ui/inputgroupaddon';

@Component({
  selector: 'currency-input-field',
  imports: [FormsModule, ReactiveFormsModule, CommonModule, InputNumber, InputGroup, InputGroupAddon],
  templateUrl: './currency-input-field.component.html',
  styles: ``
})
export class CurrencyInputFieldComponent implements ControlValueAccessor {
  readonly ngControl = inject(NgControl, { optional: true, self: true });
  readonly #currencyService = inject(CurrencyService);

  readonly id = input<string>('');
  readonly type = input<string>('text');
  readonly placeholder = input<string>('');
  readonly label = input<string>('');
  readonly errormessage = input<string>('');
  readonly control = input<AbstractControl | null>(null);
  readonly requiredLabel = input<boolean>(false);
  readonly customClass = input<string>('');
  readonly min = input<number | null>(0);
  readonly max = input<number | null>(null);
  readonly currencySymbol = input<string | null>(null);
  readonly disabled = input<boolean>(false);
  readonly uomSuffix = input<string | null>(null);

  readonly #isDisabled = signal<boolean>(false);
  readonly value = signal<number | null>(null);

  readonly displayCurrencySymbol = computed(() => {
    return this.currencySymbol() ?? this.#currencyService.currencySymbol();
  });

  /** ISO 4217 currency code for PrimeNG InputNumber (e.g. "CAD"). Never empty to avoid RangeError from Intl.NumberFormat. */
  readonly currencyCode = computed(() => this.#currencyService.currencyCode() || 'CAD');

  readonly isDisabled = computed(() => {
    return this.disabled() || this.control()?.disabled || this.#isDisabled();
  });

  readonly inputClasses = computed(() => {
    const ctrl = this.control();
    const classes = ['form-control', this.customClass()];

    if (ctrl?.valid && ctrl?.touched && ctrl?.dirty) {
      classes.push('is-valid');
    }
    if (ctrl?.invalid && ctrl?.touched && ctrl?.dirty) {
      classes.push('is-invalid');
    }

    return classes.join(' ');
  });

  readonly showError = computed(() => {
    return this.ngControl?.touched && this.ngControl?.invalid;
  });

  #onChange: (value: number | null) => void = () => {};
  #onTouched: () => void = () => {};
  /** Set when focus cleared a zero so blur can restore if the user left the field empty (without calling #onChange on focus). */
  #valueToRestoreIfEmptyOnBlur: number | undefined = undefined;

  #isEffectivelyZero(value: number | null): boolean {
    return value != null && !isNaN(value) && value === 0;
  }

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: any): void {
    if (value == null || value === '') {
      this.value.set(null);
      return;
    }
    const num = Number(value);
    this.value.set(isNaN(num) ? null : num);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.#isDisabled.set(isDisabled);
  }

  onTouched(): void {
    this.#onTouched();
  }

  onFocus(): void {
    const v = this.value();
    if (this.#isEffectivelyZero(v)) {
      this.#valueToRestoreIfEmptyOnBlur = 0;
      this.value.set(null);
    } else {
      this.#valueToRestoreIfEmptyOnBlur = undefined;
    }
  }

  onBlur(): void {
    const restore = this.#valueToRestoreIfEmptyOnBlur;
    this.#valueToRestoreIfEmptyOnBlur = undefined;

    if (restore !== undefined) {
      const current = this.value();
      const empty = current === null || current === undefined;
      if (empty) {
        // Display-only restore: focus never propagated the clear, so the form control still holds
        // `restore`. Calling #onChange here would emit a no-op valueChanges and trigger a save.
        this.value.set(restore);
      }
    }

    this.#onTouched();
  }

  onModelChange(val: number | null): void {
    this.value.set(val ?? null);
    this.#onChange(val ?? null);
  }

  readonly #allowNegative = computed(() => {
    const minValue = this.min();
    return minValue === null || minValue < 0;
  });

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value;

    const allowNeg = this.#allowNegative();
    value = allowNeg ? value.replace(/[^0-9.\-]/g, '') : value.replace(/[^0-9.]/g, '');

    if (allowNeg) {
      const leadingMinus = value.startsWith('-') ? '-' : '';
      value = leadingMinus + value.slice(leadingMinus.length).replace(/-/g, '');
    }

    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    const minValue = this.min();
    if (minValue !== null && minValue >= 0) {
      if (value.startsWith('-')) {
        value = value.replace('-', '');
        inputElement.value = value;
      }
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < minValue) {
        value = '';
        inputElement.value = '';
      }
    }

    const maxValue = this.max();
    if (maxValue !== null) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > maxValue) {
        value = maxValue.toString();
        inputElement.value = maxValue.toString();
      }
    }

    const numValue = value === '' || value === '-' ? null : isNaN(parseFloat(value)) ? null : parseFloat(value);
    this.value.set(numValue);
    this.#onChange(numValue);
  }

  onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [46, 8, 9, 27, 13, 110, 190];
    if (this.#allowNegative()) {
      allowedKeys.push(189, 109);
    }
    if (
      allowedKeys.indexOf(event.keyCode) !== -1 ||
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      (event.keyCode >= 35 && event.keyCode <= 39)
    ) {
      return;
    }
    if ((event.shiftKey || event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const pattern = this.#allowNegative() ? /[^0-9.\-]/g : /[^0-9.]/g;
    const numericValue = pastedText.replace(pattern, '');
    if (numericValue !== pastedText) {
      event.preventDefault();
      const num =
        numericValue === '' || numericValue === '-'
          ? null
          : isNaN(parseFloat(numericValue))
            ? null
            : parseFloat(numericValue);
      this.value.set(num);
      this.#onChange(num);
    }
  }
}
